import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TagIcon, TrashIcon, ListBulletIcon, Bars3Icon, ArrowsRightLeftIcon, XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import ErrorMessage from '../components/ErrorMessage';
import HelpTooltip from '../components/HelpTooltip';
import TagCloud from '../components/TagCloud';
import { bookmarkService } from '../services/bookmarkService';

const TagsPage = () => {
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const [tagsNextToken, setTagsNextToken] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('tagViewMode');
    return saved === 'graph' ? 'list' : (saved || 'list');
  });

  // Orphan cleanup state
  const [showOrphanPanel, setShowOrphanPanel] = useState(false);
  const [selectedOrphans, setSelectedOrphans] = useState(new Set());
  const [deletingBatch, setDeletingBatch] = useState(false);

  // Drag-to-merge state
  const [draggedTag, setDraggedTag] = useState(null);
  const [dragOverTag, setDragOverTag] = useState(null);
  const [mergeConfirm, setMergeConfirm] = useState(null); // { from, into }
  const [merging, setMerging] = useState(false);

  // AI consolidate state
  const [consolidating, setConsolidating] = useState(false);
  const [consolidateSuggestions, setConsolidateSuggestions] = useState([]);
  const [mergingConsolidate, setMergingConsolidate] = useState(new Set());
  const [consolidateMessage, setConsolidateMessage] = useState(null);

  useEffect(() => { loadTags(); }, []);

  const loadTags = async () => {
    try {
      setLoading(true);
      setError(null);
      const { items, nextToken } = await bookmarkService.getTags({ limit: 100 });
      setTags(items);
      setTagsNextToken(nextToken);
    } catch (err) {
      setError(err.message || 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreTags = async () => {
    if (!tagsNextToken || loadingMore) return;
    try {
      setLoadingMore(true);
      const { items, nextToken } = await bookmarkService.getTags({ limit: 100, nextToken: tagsNextToken });
      setTags(prev => [...prev, ...items]);
      setTagsNextToken(nextToken);
    } catch (err) {
      setError(err.message || 'Failed to load more tags');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleDeleteTag = async (tagName) => {
    setDeletingTag(tagName);
    try {
      await bookmarkService.deleteTag(tagName);
      setTags(prev => prev.filter(t => t.tagName !== tagName));
    } catch (err) {
      setError(err.message || 'Failed to delete tag');
    } finally {
      setDeletingTag(null);
    }
  };

  const handleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('tagViewMode', mode);
  };

  const handleNavigateToTag = (tagName) => {
    navigate('/bookmarks', { state: { filterTag: tagName } });
  };

  // ── Orphan cleanup ──────────────────────────────────────────────────────────

  const orphanTags = tags.filter(t => (t.count || 0) === 0);

  const toggleOrphan = (tagName) => {
    setSelectedOrphans(prev => {
      const next = new Set(prev);
      next.has(tagName) ? next.delete(tagName) : next.add(tagName);
      return next;
    });
  };

  const toggleAllOrphans = () => {
    setSelectedOrphans(prev =>
      prev.size === orphanTags.length
        ? new Set()
        : new Set(orphanTags.map(t => t.tagName))
    );
  };

  const handleDeleteOrphans = async () => {
    setDeletingBatch(true);
    const toDelete = [...selectedOrphans];
    for (const tagName of toDelete) {
      try {
        await bookmarkService.deleteTag(tagName);
        setTags(prev => prev.filter(t => t.tagName !== tagName));
      } catch {
        // best-effort: continue with remaining tags
      }
    }
    setSelectedOrphans(new Set());
    setShowOrphanPanel(false);
    setDeletingBatch(false);
  };

  // ── Drag-to-merge ───────────────────────────────────────────────────────────

  const handleDragStart = (e, tag) => {
    setDraggedTag(tag);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, tag) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedTag && tag.tagName !== draggedTag.tagName) {
      setDragOverTag(tag);
    }
  };

  const handleDragLeave = (e) => {
    // Only clear if leaving the row entirely (not entering a child element)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverTag(null);
    }
  };

  const handleDrop = (e, targetTag) => {
    e.preventDefault();
    if (!draggedTag || draggedTag.tagName === targetTag.tagName) return;
    setMergeConfirm({ from: draggedTag.tagName, into: targetTag.tagName });
    setDraggedTag(null);
    setDragOverTag(null);
  };

  const handleDragEnd = () => {
    setDraggedTag(null);
    setDragOverTag(null);
  };

  const confirmMerge = async () => {
    setMerging(true);
    try {
      await bookmarkService.mergeTags(mergeConfirm.from, mergeConfirm.into);
      setMergeConfirm(null);
      // Reload from server — naive count addition is wrong when a bookmark
      // had both tags (backend deduplicates, so the merged count can be less
      // than the sum of the two individual counts).
      const { items: fresh } = await bookmarkService.getTags();
      setTags(fresh);
    } catch (err) {
      setError(err.message || 'Failed to merge tags');
      setMergeConfirm(null);
    } finally {
      setMerging(false);
    }
  };

  // ── AI Consolidate ──────────────────────────────────────────────────────────

  const handleConsolidate = async () => {
    setConsolidating(true);
    setConsolidateMessage(null);
    setConsolidateSuggestions([]);
    try {
      const result = await bookmarkService.consolidateTags();
      if (!result.suggestions || result.suggestions.length === 0) {
        setConsolidateMessage('No consolidation opportunities found — your tags look good!');
      } else {
        setConsolidateSuggestions(result.suggestions);
      }
    } catch {
      setConsolidateMessage('Failed to analyse tags. Please try again.');
    } finally {
      setConsolidating(false);
    }
  };

  const handleConsolidateAccept = async (suggestion) => {
    const nonCanonical = suggestion.group.filter(t => t !== suggestion.canonical);
    const key = suggestion.group.join(',');
    setMergingConsolidate(prev => new Set(prev).add(key));
    try {
      for (const fromTag of nonCanonical) {
        await bookmarkService.mergeTags(fromTag, suggestion.canonical);
      }
      setConsolidateSuggestions(prev => prev.filter(s => s !== suggestion));
      const { items: fresh } = await bookmarkService.getTags();
      setTags(fresh);
    } catch (err) {
      setError(`Failed to merge tags: ${err.message}`);
    } finally {
      setMergingConsolidate(prev => { const next = new Set(prev); next.delete(key); return next; });
    }
  };

  const handleConsolidateSkip = (suggestion) => {
    setConsolidateSuggestions(prev => prev.filter(s => s !== suggestion));
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="space-y-4">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Tags</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">{tags.length}{tagsNextToken ? '+' : ''} tag{tags.length !== 1 ? 's' : ''}</p>
          </div>
          {tags.length >= 2 && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <button
                  onClick={handleConsolidate}
                  disabled={consolidating}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 rounded-lg transition-colors disabled:opacity-50"
                  title="Use AI to find and suggest similar tags to merge"
                >
                  <SparklesIcon className="h-4 w-4" />
                  {consolidating ? 'Analysing…' : 'Consolidate'}
                </button>
                <HelpTooltip id="tags-consolidate" />
              </div>
              <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                <button
                  onClick={() => handleViewMode('list')}
                  title="List view"
                  className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-primary-600 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                >
                  <ListBulletIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleViewMode('cloud')}
                  title="Cloud view"
                  className={`p-2 transition-colors ${viewMode === 'cloud' ? 'bg-primary-600 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                >
                  <TagIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Orphan cleanup banner */}
        {orphanTags.length > 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3 flex items-center justify-between gap-3">
            <p className="text-sm text-amber-800 dark:text-amber-300">
              <span className="font-medium">{orphanTags.length} unused tag{orphanTags.length !== 1 ? 's' : ''}</span>
              {' '}— not associated with any bookmark
            </p>
            <button
              onClick={() => {
                setShowOrphanPanel(v => !v);
                if (!showOrphanPanel) setSelectedOrphans(new Set(orphanTags.map(t => t.tagName)));
              }}
              className="text-sm font-medium text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-300 whitespace-nowrap transition-colors"
            >
              {showOrphanPanel ? 'Hide' : 'Clean up →'}
            </button>
          </div>
        )}

        {/* Orphan cleanup panel */}
        {showOrphanPanel && orphanTags.length > 0 && (
          <div className="bg-white dark:bg-gray-800 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={selectedOrphans.size === orphanTags.length}
                  onChange={toggleAllOrphans}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Select all ({orphanTags.length})
              </label>
              <button
                onClick={handleDeleteOrphans}
                disabled={selectedOrphans.size === 0 || deletingBatch}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-40"
              >
                <TrashIcon className="h-4 w-4" />
                {deletingBatch ? 'Deleting…' : `Delete ${selectedOrphans.size > 0 ? `(${selectedOrphans.size})` : ''}`}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {orphanTags.map(tag => (
                <label
                  key={tag.tagName}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border cursor-pointer select-none transition-colors ${
                    selectedOrphans.has(tag.tagName)
                      ? 'bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-700 text-red-700 dark:text-red-400'
                      : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedOrphans.has(tag.tagName)}
                    onChange={() => toggleOrphan(tag.tagName)}
                    className="sr-only"
                  />
                  {selectedOrphans.has(tag.tagName) && <XMarkIcon className="h-3 w-3" />}
                  {tag.tagName}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* AI consolidate message / suggestions */}
        {consolidateMessage && (
          <div className="bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-100 dark:border-indigo-800 rounded-lg px-4 py-3 text-sm text-indigo-700 dark:text-indigo-300">
            {consolidateMessage}
          </div>
        )}
        {consolidateSuggestions.length > 0 && (
          <div className="space-y-2 rounded-lg border border-indigo-100 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 p-4">
            <p className="text-sm font-medium text-indigo-700 dark:text-indigo-300 flex items-center gap-1.5">
              <SparklesIcon className="h-4 w-4" /> Suggested consolidations
            </p>
            {consolidateSuggestions.map(s => {
              const key = s.group.join(',');
              const isMerging = mergingConsolidate.has(key);
              return (
                <div key={key} className="rounded-lg bg-white dark:bg-gray-800 border border-indigo-100 dark:border-indigo-800 p-3 shadow-sm">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Merge <span className="font-medium">{s.group.filter(t => t !== s.canonical).join(', ')}</span>
                    {' → '}
                    <span className="font-medium text-indigo-700 dark:text-indigo-400">{s.canonical}</span>
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{s.reason}</p>
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={() => handleConsolidateAccept(s)}
                      disabled={isMerging}
                      className="px-3 py-1 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                    >
                      {isMerging ? 'Merging…' : 'Accept'}
                    </button>
                    <button
                      onClick={() => handleConsolidateSkip(s)}
                      disabled={isMerging}
                      className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                    >
                      Skip
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {error && (
          <ErrorMessage message={error} onRetry={() => { setError(null); loadTags(); }} />
        )}

        {tags.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
            <TagIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No tags yet</h3>
            <p className="text-gray-600 dark:text-gray-400">Tags are created automatically when you add bookmarks.</p>
          </div>
        ) : viewMode === 'list' ? (
          <>
            {tags.length > 1 && !draggedTag && (
              <p className="text-xs text-center text-gray-400 dark:text-gray-500">
                Drag a tag onto another to merge them
              </p>
            )}
            {draggedTag && (
              <p className="text-xs text-center text-gray-400 dark:text-gray-500 animate-pulse">
                Drop <span className="font-medium text-gray-600 dark:text-gray-300">"{draggedTag.tagName}"</span> onto another tag to merge it
              </p>
            )}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm divide-y divide-gray-100 dark:divide-gray-700">
              {tags.map(tag => {
                const isDragging = draggedTag?.tagName === tag.tagName;
                const isDropTarget = dragOverTag?.tagName === tag.tagName;
                return (
                  <div
                    key={tag.tagName}
                    draggable
                    onDragStart={e => handleDragStart(e, tag)}
                    onDragOver={e => handleDragOver(e, tag)}
                    onDragLeave={handleDragLeave}
                    onDrop={e => handleDrop(e, tag)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center justify-between px-4 py-3 transition-colors ${
                      isDragging ? 'opacity-40 bg-gray-50 dark:bg-gray-700' :
                      isDropTarget ? 'bg-indigo-50 dark:bg-indigo-900/30 border-l-4 border-indigo-400' :
                      draggedTag ? 'hover:bg-indigo-50 dark:hover:bg-indigo-900/30 cursor-copy' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Bars3Icon className={`h-4 w-4 flex-shrink-0 cursor-grab active:cursor-grabbing ${
                        isDropTarget ? 'text-indigo-400' : 'text-gray-300'
                      }`} />
                      <span
                        onClick={() => !draggedTag && handleNavigateToTag(tag.tagName)}
                        className={`px-3 py-1 rounded-full text-sm font-medium cursor-pointer transition-colors ${
                          isDropTarget
                            ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300'
                            : 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-900/50'
                        }`}
                      >
                        {tag.tagName}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{tag.count} bookmark{tag.count !== 1 ? 's' : ''}</span>
                      {isDropTarget && (
                        <span className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                          <ArrowsRightLeftIcon className="h-3.5 w-3.5" />
                          Merge "{draggedTag.tagName}" into this tag
                        </span>
                      )}
                    </div>
                    {!draggedTag && (
                      <button
                        onClick={() => handleDeleteTag(tag.tagName)}
                        disabled={deletingTag === tag.tagName}
                        className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                        aria-label={`Delete tag ${tag.tagName}`}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            {tags.length > 1 && !draggedTag && (
              <p className="text-xs text-center text-gray-400 dark:text-gray-500">
                Drag a tag onto another to merge them
              </p>
            )}
            {draggedTag && (
              <p className="text-xs text-center text-gray-400 dark:text-gray-500 animate-pulse">
                Drop <span className="font-medium text-gray-600 dark:text-gray-300">"{draggedTag.tagName}"</span> onto another tag to merge it
              </p>
            )}
            <TagCloud
            tags={tags}
            onNavigate={handleNavigateToTag}
            dragHandlers={tags.length > 1 ? {
              draggedTag,
              dragOverTag,
              onDragStart: handleDragStart,
              onDragOver: handleDragOver,
              onDragLeave: handleDragLeave,
              onDrop: handleDrop,
              onDragEnd: handleDragEnd,
            } : undefined}
            />
          </>
        )}
      </div>

      {/* Load more tags */}
      {tagsNextToken && (
        <div className="flex justify-center pt-2">
          <button
            onClick={loadMoreTags}
            disabled={loadingMore}
            className="px-5 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            {loadingMore ? 'Loading…' : 'Load more tags'}
          </button>
        </div>
      )}

      {/* Merge confirmation dialog */}
      {mergeConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-sm w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center">
                <ArrowsRightLeftIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Merge tags?</h2>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              All bookmarks tagged{' '}
              <span className="inline-flex items-center px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 rounded-full text-xs font-medium">{mergeConfirm.from}</span>
              {' '}will be retagged as{' '}
              <span className="inline-flex items-center px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 rounded-full text-xs font-medium">{mergeConfirm.into}</span>.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              The tag <span className="font-medium">"{mergeConfirm.from}"</span> will be permanently removed.
            </p>
            <div className="flex gap-3">
              <button
                onClick={confirmMerge}
                disabled={merging}
                className="flex-1 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                {merging ? 'Merging…' : 'Merge tags'}
              </button>
              <button
                onClick={() => setMergeConfirm(null)}
                disabled={merging}
                className="flex-1 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagsPage;
